<script setup>
import { notifyError } from "@/utils/notificationUtils";
import validationRules from "@/utils/validation-rules.js";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { useUnitDisplay } from "@/composables/useUnitDisplay";
import LineWorksCrossSectionInput from "./LineWorksCrossSectionInput.vue";
import { defaultLineWorksCrossSectionByDistanceUnit } from "./default";

const props = defineProps({
	viewerLineId: String,
	type: Number,
});

const emit = defineEmits(["close"]);

const isValid = ref(false);
const name = ref("");
const isVisible = ref(true);
const isArbitraryAdded = ref(false);

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const { siteUnits, fromDisplayDistance } = useUnitDisplay();

const createDefaultCrossSection = () =>
	defaultLineWorksCrossSectionByDistanceUnit(
		siteUnits.value.distance,
		fromDisplayDistance,
	);

const crossSectionData = ref(createDefaultCrossSection());

const rules = {
	...validationRules,
	required: (value) => !!value || "*",
	validName: (v) => !/[~@#$^*()+=[\]{}|\\,?:<>!'"/;&₹€`%]/.test(v),
};

const addArbitraryLineDisabled = computed(() => {
	return name.value === null || name.value === "" || /^\s*$/.test(name.value);
});

const closeDialog = () => {
	window["viewer"].cancelArbitraryCreation(props.viewerLineId);
	emit("close", false);
};
const resetProperty = () => {
	crossSectionData.value = createDefaultCrossSection();
	isVisible.value = false;
	isValid.value = false;
	isArbitraryAdded.value = false;
};

const addArbitraryLine = async () => {
	isArbitraryAdded.value = true;
	try {
		// ローディング開始
		await store.dispatch("executeWithSpinner", async () => {
			try {
				const data = {
					lineInfo: {
						viewerLineId: props.viewerLineId,
						label: name.value,
						json: store.state.viewer.getArbitraryLineJSON(props.viewerLineId),
						siteId: route.query.siteId,
						isVisible: isVisible.value,
					},
					crossSectionSetting: { ...crossSectionData.value },
				};
				await store.dispatch("arbitraryLine/addArbitraryLine", data);
			} catch (e) {
				window["viewer"].deleteArbitraryLine(props.viewerLineId);
				notifyError(
					{ $store: store, $t: t },
					t("LINEWORKS_CREATION_FAILED", { name: name.value }),
				);
			}
		});
	} finally {
		emit("close", false);
	}
};

defineExpose({
	resetProperty,
});
</script>

<template>
	<v-card class="px-4 pt-2 pb-4" color="rgba(41, 41, 47, 0.8)">
		<v-card-title> {{ $t("ADD_LINEWORK_INFO") }} </v-card-title>
		<v-card-text>
			<v-form v-model="isValid">
				<v-table density="compact" style="background-color: rgba(41, 41, 47, 0)">
					<tbody>
						<tr>
							<td class="linear-name">
								{{ $t("ArbitraryLinename") }}
							</td>
							<td>
								<v-text-field hide-details density="compact" v-model="name" :rules="[rules.validName]" autofocus />
							</td>
						</tr>
					</tbody>
				</v-table>
				<v-container>
					<v-row class="justify-center">
						<line-works-cross-section-input :crossSectionData="crossSectionData"></line-works-cross-section-input>
					</v-row>
					<v-row>
						<v-card-actions class="full-width justify-end mt-2">
							<v-btn variant="flat" @click="closeDialog" color="red">
								{{ $t("CANCEL") }}
							</v-btn>
							<v-btn variant="flat" @click="addArbitraryLine()" color="primary"
								:disabled="!isValid || addArbitraryLineDisabled || isArbitraryAdded">
								{{ $t("ADD") }}
							</v-btn>
						</v-card-actions>
					</v-row>
				</v-container>
			</v-form>
		</v-card-text>
	</v-card>
</template>


<style scoped>
.linear-name {
	width: 40%;
}

.mt-0 {
	margin-top: 0;
}

.mt-2 {
	margin-top: 2em;
}

.pt-2 {
	padding-top: 2em;
}

.full-width {
	width: 100%;
}
</style>